import { ApolloError } from '@apollo/client';
import { Box, Typography, Snackbar, Grid, Alert } from '@mui/material';
import {
  DataGridPro,
  getGridStringOperators,
  gridFilteredSortedRowIdsSelector,
  GridCellEditCommitParams,
  GridCellParams,
  GridColumnVisibilityModel,
  GridPinnedColumns,
  GridRenderCellParams,
  GridRowId,
  GridSelectionModel,
  GridSortModel,
  GridDensity,
  useGridApiRef,
} from '@mui/x-data-grid-pro';

import { format } from 'date-fns';
import { Maybe } from 'graphql/jsutils/Maybe';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import '../App.css';
import {
  DocumentFile,
  RouteType,
  useDeleteAttachmentFileMutation,
  useTruckFillAndTimeMutation,
  useRefetchRouteLegLazyQuery,
  RecievedStatus,
} from '../generated/graphql';
import {
  COUNTRIES_MAP,
  TABLE_NAMES,
  TRAFFIC_TYPES,
  TrafficType,
} from '../lib/constants';
import { DATE_FORMAT, timeOrNull } from '../lib/date_time';
import {
  CustomColumnsPanel,
  doesNotContainOperator,
  doesNotEqualOperator,
} from './datagrid/UserConfiguredDataGridPro';
import _ from 'lodash';
import { UploadFileModal } from './modal/UploadFileModal';
import { useHttpClient } from '../providers/HttpClientProvider';
import { TftCustomGridToolbar } from './TFTCustomGridToolBar';
import * as XLSX from 'xlsx';
import { makeStyles, createStyles } from '@mui/styles';
import { useUserConfiguration } from '../providers/UserConfigurationProvider';
import { listIncomingColumns } from './ListIncomingColumn';
import { TftReadOnlyConfigType } from '../views/TruckFillAndTime';
import {
  TerminalStatusModal,
  TerminalStatusModalUpdate,
} from './datagrid/TerminalStatusModal';
import {
  formatTerminalStatusDescriptionFromLog,
  getLatestTerminalStatusLogForTerminal,
  getDoneStatusSelections,
} from '../utils/terminalStatusOptions';
import { resolveLoadTypeFromLeg } from '../utils/checkpointUtils';
import { printLoadingLists } from './PrintLoadingLists';

const useStylesIncomingRoutes = makeStyles(() =>
  createStyles({
    editableCell: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%',
      '& p:first-child': {
        maxWidth: '75%',
        wordWrap: 'break-word',
        overflowWrap: 'break-word',
        inlineSize: '75%',
      },
    },
    iconStyle: {
      padding: '0 !important',
    },
    muiOdd: {
      '&:hover': {
        backgroundColor: '#e7e7e7',
      },
      backgroundColor: '#e7e7e7',
    },
    muiEven: {
      '&:hover': {
        backgroundColor: '#fff',
      },
      backgroundColor: '#fff',
    },
    muiCurrent: {
      '&:hover': {
        backgroundColor: '#9ee7a4 !important',
      },
      backgroundColor: '#9ee7a4 !important',
    },
    muiLateStatusCell: {
      '&:hover': {
        background: '#e89992 !important',
      },
      backgroundColor: '#f5c6c6',
    },
  }),
);

interface TerminalLocation {
  id: number;
  shortCountryCode: string;
}

interface TerminalStatusLogEntry {
  id: string;
  status: string;
  timestamp: string;
  legId: number;
  locationId: number;
  locationOrder: number;
  locationLabel: string;
  locationName: string;
  createdBy: string;
  updatedBy?: string | null;
  updatedAt?: string | null;
  gate?: string | null;
}

interface ListIncomingRoutesProps {
  gridBoxHeight: number;
  isHideRegistered: boolean;
  isHideKippRegistered: boolean;
  isHideDone: boolean;
  location: {
    id: number;
  };
  legs?: {
    id: number;
    arrivalTime: string;
    departureTime?: Maybe<string>;
    actualArrivalTime?: Maybe<string>;
    actualArrivalDate?: Maybe<string>;
    actualDepartureTime?: Maybe<string>;
    actualDepartureDate?: Maybe<string>;
    actualDockTime?: Maybe<string>;
    actualDockDate?: Maybe<string>;
    actualUndockTime?: Maybe<string>;
    actualUndockDate?: Maybe<string>;
    productionDate: string;
    transportationDate: string;
    actualCapacity?: Maybe<number>;
    deletedAt: Maybe<string>;
    position?: number;
    loadCar?: Maybe<number>;
    loadCages?: Maybe<number>;
    loadTrailer?: Maybe<number>;
    unloadCar?: Maybe<number>;
    unloadCages?: Maybe<number>;
    unloadTrailer?: Maybe<number>;
    loadCarPPL?: Maybe<number>;
    loadTrailerPPL?: Maybe<number>;
    unloadCarPPL?: Maybe<number>;
    unloadTrailerPPL?: Maybe<number>;
    arrivingPallets?: Maybe<number>;
    arrivingPalletSpace?: Maybe<number>;
    containerId?: Maybe<string> | undefined;
    togref?: Maybe<string> | undefined;
    timeObj?: {
      originalArrTime: string;
      actualArrTime: string;
      originalDepartTime: string;
      actualDepartTime: string;
      terminalName: string;
      legId: number;
      transportationDate: string;
    }[];
    note: string;
    unload: boolean;
    load: boolean;
    gateNumber?: Maybe<string> | undefined;
    carRegistrationNumber?: Maybe<string> | undefined;
    trailerRegistrationNumber?: Maybe<string> | undefined;
    terminalNickname?: Maybe<string> | undefined;
    terminalNote: string;
    lastUpdatedBy?: Maybe<string>;
    updatedAt: string;
    isNonArrivalMarked?: boolean;
    nonArrivalMarkedFrom?: Maybe<string>;
    isSealBroken?: Maybe<boolean>;
    sealNumber?: Maybe<string> | undefined;
    productionGroup?: Maybe<string> | undefined;
    operator?: Maybe<string> | undefined;
    terminalResponsible?: Maybe<string> | undefined;
    priority?: Maybe<string> | undefined;
    terminalStatus?: Maybe<string> | undefined;
    terminalStatusDescription?: Maybe<string> | undefined;
    terminalStatusUpdatedAt?: Maybe<string> | undefined;
    terminalStatusLog?: Maybe<TerminalStatusLogEntry[]> | undefined;
    arrivalStatus?: Maybe<string> | undefined;
    transportType?: Maybe<string> | undefined;
    isLoadingInCar?: boolean;
    isLoadingInTrailer?: boolean;
    receivedStatus?: Maybe<RecievedStatus> | undefined;
    receivedBy?: Maybe<string> | undefined;
    timeWhenReceived?: Maybe<string> | undefined;
    kippReceivedBy?: Maybe<string> | undefined;
    kippTimeWhenReceived?: Maybe<string> | undefined;
    assigned?: Maybe<boolean> | undefined;
    ansvBefraktare?: Maybe<string> | undefined;
    timeWhenAssigned?: Maybe<string> | undefined;
    firstLegCarDetailId?: Maybe<number> | undefined;
    firstLegId?: Maybe<number> | undefined;
    carDetails?: {
      id?: number | undefined;
      actualCapacity?: Maybe<number> | undefined;
      registrationNumber?: Maybe<string> | undefined;
      gateNumber?: Maybe<string> | undefined;
      togref?: Maybe<string> | undefined;
      containerId?: Maybe<string> | undefined;
      loadingListItem?:
        | Maybe<
            Array<{
              id?: number;
              volume?: Maybe<{
                bpx?: Maybe<number>;
                pri03?: Maybe<number>;
                pri49?: Maybe<number>;
                upri03?: Maybe<number>;
                upri49?: Maybe<number>;
                parti?: Maybe<number>;
                brev?: Maybe<number>;
                pru?: Maybe<number>;
                pip?: Maybe<number>;
              }>;
            }>
          >
        | undefined;
    };
    trailerDetails?: {
      actualCapacity?: Maybe<number> | undefined;
      registrationNumber?: Maybe<string> | undefined;
      gateNumber?: Maybe<string> | undefined;
    };
    files: Pick<
      DocumentFile,
      'id' | 'size' | 'path' | 'mimetype' | 'originalname'
    >[];
    loadingListItems: {
      pallets?: Maybe<number>;
      palletSpace?: Maybe<number>;
      isDangerous: boolean;
    }[];
    location?: Maybe<{
      id: number;
      city: string;
      country: string;
      locationShortCode: string;
      type: string;
    }>;
    bpx?: Maybe<number> | undefined;
    pri03?: Maybe<number> | undefined;
    pri49?: Maybe<number> | undefined;
    upri03?: Maybe<number> | undefined;
    upri49?: Maybe<number> | undefined;
    parti?: Maybe<number> | undefined;
    brev?: Maybe<number> | undefined;
    pru?: Maybe<number> | undefined;
    pip?: Maybe<number> | undefined;
    tom?: Maybe<boolean>;
    driverName?: Maybe<string>;
    route: {
      id: number;
      isCreatedFromTerminal: boolean;
      isCreatedFromPlanned: boolean;
      type: RouteType;
      routeId: string;
      capacity?: Maybe<number>;
      isUnplannedRoute: boolean;
      routeCreatedBy: string;
      routeLastUpdatedBy: string;
      routeUpdatedAt: Date;
      routeDeletedAt: Date;
      routeDeletedBy: string;
      trainNumber?: string;
      containerId?: string;
      togref?: string;
      inneholdsbeskrivelse?: string;
      turnummer?: Maybe<number> | undefined;
      rutekode?: Maybe<string> | undefined;
      adr?: string;
      tourRoute?: Maybe<{
        tourId: number;
      }>;
      transportationDate: string;
      driverName: string;
      trailerCarrierId: string;
      carCarrierId: string;
      subcontractorLocation: {
        id: number;
        name: string;
      };
      legs: {
        position?: Maybe<number>;
        unload: boolean;
        load: boolean;
        deletedAt: Maybe<string>;
        location?: Maybe<{
          id: number;
          name: string;
          city: string;
          country: string;
          locationShortCode: string;
        }>;
      }[];
    };
  }[];
  selectedLocation?: TerminalLocation;
  loading: boolean;
  error?: ApolloError;
  group?: string;
  tftConfig: TftReadOnlyConfigType;
  setTftConfig: any;
  onRefreshData?: () => void;
  onTerminalStatusUpdated?: (updatedRow: TerminalStatusModalUpdate) => void;
  onNorwayColumnsUpdated?: (updatedRow: {
    id: string | number;
    legId?: number;
    [key: string]: any;
  }) => void;
  selectedTrafficType?: string;
}

function normalizeGridTextValue(value?: string | null) {
  return value ?? '';
}

function normalizeGridStatusLog(value?: TerminalStatusLogEntry[] | null) {
  return value ?? [];
}

export function ListIncomingRoutes(props: ListIncomingRoutesProps) {
  const classes = useStylesIncomingRoutes();
  const apiRef = useGridApiRef();
  const { t } = useTranslation();
  const density: GridDensity = 'compact';
  const { globalDateFormat, getCustomConfiguration } = useUserConfiguration();
  const [filteredRowsData, setFilteredRowsData] = useState<
    Record<string, boolean>
  >({});

  const height = props.gridBoxHeight;
  const [errorInfo, setErrorInfo] = useState<{
    errorInField: boolean;
    errorMessage: string;
  }>();
  const [autoHeights, setAutoHeights] = useState<boolean>(false);
  const [openUploadModal, setOpenUploadModal] = useState<boolean>(false);
  const [showUploadLoading, setShowUploadLoading] = useState<boolean>(false);
  const [activeRowId, setActiveRowId] = useState<GridRowId | null>(null);
  const [selectedRowIds, setSelectedRowIds] = useState<GridSelectionModel>([]);
  const [isPreparingPrint, setIsPreparingPrint] = useState(false);

  const [activeTerminalStatusLegId, setActiveTerminalStatusLegId] = useState<
    number | null
  >(null);
  const [extraData, setExtraData] = useState<{
    routeName: string;
    legId: number;
    subcontractorName: string;
    existingFiles: DocumentFile[];
  }>();
  const { httpClient } = useHttpClient();
  const { tftConfig, setTftConfig } = props;

  const expandRowHeight = tftConfig.expandRowHeight ?? false;
  const [isAdjustedColumnWidth, setIsAdjustedColumnWidth] = useState(false);
  const originalColumnWidthsRef = useRef<Record<string, number> | null>(null);
  const applyingColumnWidthToggleRef = useRef(false);

  const setExpandRowHeight = (val: boolean | ((prev: boolean) => boolean)) => {
    setTftConfig((prev: TftReadOnlyConfigType) => {
      const previousExpandRowHeight = prev.expandRowHeight ?? false;
      return {
        ...prev,
        expandRowHeight:
          typeof val === 'function' ? val(previousExpandRowHeight) : val,
      };
    });
  };

  // Ref tracks the latest visibility model so column quick-filter functions
  // can check it at evaluation time without needing new column objects.
  const visibilityRef = useRef<GridColumnVisibilityModel>({});

  visibilityRef.current = tftConfig.gridColumnVisibilityModel ?? {};
  const [configStatus, setConfigStatus] = useState({
    status: '',
  });
  const { legs, loading, error, location, selectedLocation, group } = props;

  // Tracks the previous legs prop so the sync effect can skip rows whose
  // underlying leg data hasn't changed (preserves local edits on other rows).
  const prevLegsRef = useRef(legs);
  const rows = React.useMemo(
    () => buildRows(legs, selectedLocation?.id ?? 0),
    [legs, selectedLocation?.id],
  );

  const densitySizing: Record<
    GridDensity,
    { rowHeight: number; headerHeight: number; fontSize: number }
  > = {
    compact: { rowHeight: 40, headerHeight: 46, fontSize: 13 },
    standard: { rowHeight: 50, headerHeight: 56, fontSize: 14.5 },
    comfortable: { rowHeight: 62, headerHeight: 66, fontSize: 16 },
  };
  const densityConfig = densitySizing[density];
  const scaledRowHeight = Math.round(densityConfig.rowHeight);
  const scaledHeaderHeight = Math.round(densityConfig.headerHeight);
  const scaledFontSize = densityConfig.fontSize;

  const [refetchLatestLeg] = useRefetchRouteLegLazyQuery({
    fetchPolicy: 'no-cache',
  });
  const [deleteAttachmentFileMutation] = useDeleteAttachmentFileMutation();
  const [rowsData, setRowsData] = useState(rows ?? []);
  const [truckFillAndTimeMutation] = useTruckFillAndTimeMutation();

  const rawDoneStatuses = getCustomConfiguration(
    'terminalStatusSettings',
    'doneStatuses',
  );
  const configuredDoneStatuses = React.useMemo(() => {
    if (!rawDoneStatuses || !location?.id) {
      return [];
    }
    const doneStatusesByTerminal = rawDoneStatuses as Record<string, unknown>;

    const terminalDoneStatuses =
      doneStatusesByTerminal[String(location.id)] ?? [];

    return getDoneStatusSelections(terminalDoneStatuses);
  }, [rawDoneStatuses, location.id]);

  React.useEffect(() => {
    let gridRows = [...rowsData];
    if (group && group !== 'All-Groups') {
      gridRows = [
        ...gridRows,
        ...rows.filter((i) => !gridRows.some((d) => d.legId === i.legId)),
      ];
      setRowsData(gridRows.filter((leg) => leg.productionGroup === group));
    } else {
      gridRows = [
        ...gridRows,
        ...rows.filter((i) => !gridRows.some((d) => d.legId === i.legId)),
      ];
      setRowsData(gridRows);
    }

    if (rows && rows.length > 3) {
      setAutoHeights(false);
    } else {
      setAutoHeights(true);
    }
  }, [group]);

  const selectedTerminalStatusRow = React.useMemo(
    () =>
      activeTerminalStatusLegId == null
        ? null
        : ((rowsData.find((row) => row.legId === activeTerminalStatusLegId) ??
            null) as
            | React.ComponentProps<typeof TerminalStatusModal>['row']
            | null),
    [activeTerminalStatusLegId, rowsData],
  );

  const handleCellChecked = React.useCallback(
    async (id: any, isChecked: boolean, field) => {
      const legId = id ? parseInt(id) : 0;
      if (legId !== 0) {
        await truckFillAndTimeMutation({
          variables: {
            truckFillAndTimeInput: {
              id: legId,
              locationId: location.id,
              [field]: isChecked,
            },
          },
        });
      }
    },
    [rows],
  );
  const handleSealBrokenChecked = React.useCallback(
    async (id: any, isChecked: boolean) => {
      const legId = id ? parseInt(id) : 0;
      if (legId !== 0) {
        await truckFillAndTimeMutation({
          variables: {
            truckFillAndTimeInput: {
              id: legId,
              locationId: location.id,
              isSealBroken: isChecked,
            },
          },
        });
      }
    },
    [rows],
  );

  const handleColumnOrderChange = () => {
    const columnOrder = apiRef.current.state.columns.all;
    setTftConfig((prev: any) => ({ ...prev, columnOrder }));
  };

  const handleToggleColumnWidth = useCallback(() => {
    if (!isAdjustedColumnWidth) {
      // Capture the actual grid widths so we can restore the user's layout.
      const originalWidths: Record<string, number> = {};

      apiRef.current.getAllColumns().forEach((column) => {
        if (column.field.startsWith('__')) {
          return;
        }

        originalWidths[column.field] = Math.max(
          column.width ?? 100,
          column.minWidth ?? 0,
        );
      });

      originalColumnWidthsRef.current = originalWidths;
      setIsAdjustedColumnWidth(true);
    } else {
      setIsAdjustedColumnWidth(false);
    }
  }, [apiRef, isAdjustedColumnWidth]);

  useEffect(() => {
    const originalWidths = originalColumnWidthsRef.current;
    if (!originalWidths) {
      return;
    }

    applyingColumnWidthToggleRef.current = true;

    try {
      if (isAdjustedColumnWidth) {
        // Expand narrow columns without shrinking any columns already wider.
        // Most columns need a minimum expanded width of 150px. Some longer
        // headers need more space to display their full names.
        const expandedColumnWidths: Record<string, number> = {
          timeWhenReceived: 240,
          kippTimeWhenReceived: 240,
        };

        Object.entries(originalWidths).forEach(([field, originalWidth]) => {
          if (apiRef.current.getColumn(field)) {
            const targetWidth = expandedColumnWidths[field] ?? 150;
            apiRef.current.setColumnWidth(
              field,
              Math.max(originalWidth, targetWidth),
            );
          }
        });
        return;
      }

      // Restore the exact widths captured before the toggle was enabled.
      Object.entries(originalWidths).forEach(([field, width]) => {
        if (apiRef.current.getColumn(field)) {
          apiRef.current.setColumnWidth(field, width);
        }
      });

      originalColumnWidthsRef.current = null;
    } finally {
      applyingColumnWidthToggleRef.current = false;
    }
  }, [apiRef, isAdjustedColumnWidth]);

  const handleWidthChange = useCallback(() => {
    // The temporary widths applied by this toggle must not overwrite saved widths.
    if (isAdjustedColumnWidth || applyingColumnWidthToggleRef.current) {
      return;
    }

    const lookupObj = apiRef?.current?.state?.columns?.lookup || {};
    const lookup: Record<string, { width: number }> = {};

    for (const key in lookupObj) {
      if (Object.prototype.hasOwnProperty.call(lookupObj, key)) {
        const width = lookupObj[key]?.width;
        if (typeof width === 'number' && Number.isFinite(width)) {
          lookup[key] = { width };
        }
      }
    }

    setTftConfig((prev: any) =>
      prev ? { ...prev, columnsWidth: lookup } : prev,
    );
  }, [apiRef, setTftConfig, isAdjustedColumnWidth]);

  const handleNorwayColumnsUpdate = useCallback(
    (updatedFields: { id: string | number; [key: string]: any }) => {
      props.onNorwayColumnsUpdated?.(updatedFields);

      setRowsData((prev) =>
        prev.map((r) =>
          r.id === updatedFields.id ? { ...r, ...updatedFields, id: r.id } : r,
        ),
      );
    },
    [props.onNorwayColumnsUpdated],
  );

  const handleTerminalStatusUpdated = useCallback(
    (updatedStatus: TerminalStatusModalUpdate) => {
      props.onTerminalStatusUpdated?.(updatedStatus);

      setRowsData((prev) => {
        let didChange = false;

        const nextRows = prev.map((row) => {
          const sameRoute = row.routeId === updatedStatus.routeId;
          const sameLeg = row.legId === updatedStatus.legId;

          if (!sameRoute && !sameLeg) {
            return row;
          }

          let nextRow = row;

          const assign = (key: keyof typeof row, value: any) => {
            if (nextRow === row) {
              nextRow = { ...row };
            }

            (nextRow as any)[key] = value;
            didChange = true;
          };

          if (
            sameLeg &&
            updatedStatus.terminalStatus !== undefined &&
            updatedStatus.terminalStatus !== row.terminalStatus
          ) {
            assign('terminalStatus', updatedStatus.terminalStatus);
          }

          if (
            sameLeg &&
            updatedStatus.terminalStatusDescription !== undefined
          ) {
            const nextTerminalStatusDescription = normalizeGridTextValue(
              updatedStatus.terminalStatusDescription,
            );

            if (
              nextTerminalStatusDescription !== row.terminalStatusDescription
            ) {
              assign(
                'terminalStatusDescription',
                nextTerminalStatusDescription,
              );
            }
          }

          if (sameLeg && updatedStatus.terminalStatusUpdatedAt !== undefined) {
            const nextTerminalStatusUpdatedAt = normalizeGridTextValue(
              updatedStatus.terminalStatusUpdatedAt,
            );

            if (nextTerminalStatusUpdatedAt !== row.terminalStatusUpdatedAt) {
              assign('terminalStatusUpdatedAt', nextTerminalStatusUpdatedAt);
            }
          }

          if (sameLeg && updatedStatus.terminalStatusLog !== undefined) {
            const nextTerminalStatusLog = normalizeGridStatusLog(
              updatedStatus.terminalStatusLog,
            );

            if (!_.isEqual(nextTerminalStatusLog, row.terminalStatusLog)) {
              const latestTerminalStatusLog =
                getLatestTerminalStatusLogForTerminal(
                  nextTerminalStatusLog,
                  location.id,
                );
              assign('terminalStatusLog', nextTerminalStatusLog);
              assign(
                'currentTerminalStatus',
                latestTerminalStatusLog?.status ?? '',
              );
              assign(
                'currentTerminalStatusDescription',
                formatTerminalStatusDescriptionFromLog(latestTerminalStatusLog),
              );
            }
          }

          if (sameLeg && updatedStatus.actualArrivalDate !== undefined) {
            const nextActualArrivalDate = normalizeGridTextValue(
              updatedStatus.actualArrivalDate,
            );

            if (nextActualArrivalDate !== row.actualArrivalDate) {
              assign('actualArrivalDate', nextActualArrivalDate);
            }
          }

          if (sameLeg && updatedStatus.actualArrivalTime !== undefined) {
            const nextActualArrivalTime = normalizeGridTextValue(
              updatedStatus.actualArrivalTime,
            );

            if (nextActualArrivalTime !== row.actualArrivalTime) {
              assign('actualArrivalTime', nextActualArrivalTime);
            }
          }

          if (sameLeg && updatedStatus.actualDepartureDate !== undefined) {
            const nextActualDepartureDate = normalizeGridTextValue(
              updatedStatus.actualDepartureDate,
            );

            if (nextActualDepartureDate !== row.actualDepartureDate) {
              assign('actualDepartureDate', nextActualDepartureDate);
            }
          }

          if (sameLeg && updatedStatus.actualDepartureTime !== undefined) {
            const nextActualDepartureTime = normalizeGridTextValue(
              updatedStatus.actualDepartureTime,
            );

            if (nextActualDepartureTime !== row.actualDepartureTime) {
              assign('actualDepartureTime', nextActualDepartureTime);
            }
          }

          if (sameLeg && updatedStatus.actualDockDate !== undefined) {
            const nextActualDockDate = normalizeGridTextValue(
              updatedStatus.actualDockDate,
            );

            if (nextActualDockDate !== row.actualDockDate) {
              assign('actualDockDate', nextActualDockDate);
            }
          }

          if (sameLeg && updatedStatus.actualDockTime !== undefined) {
            const nextActualDockTime = normalizeGridTextValue(
              updatedStatus.actualDockTime,
            );

            if (nextActualDockTime !== row.actualDockTime) {
              assign('actualDockTime', nextActualDockTime);
            }
          }

          if (sameLeg && updatedStatus.actualUndockDate !== undefined) {
            const nextActualUndockDate = normalizeGridTextValue(
              updatedStatus.actualUndockDate,
            );

            if (nextActualUndockDate !== row.actualUndockDate) {
              assign('actualUndockDate', nextActualUndockDate);
            }
          }

          if (sameLeg && updatedStatus.actualUndockTime !== undefined) {
            const nextActualUndockTime = normalizeGridTextValue(
              updatedStatus.actualUndockTime,
            );

            if (nextActualUndockTime !== row.actualUndockTime) {
              assign('actualUndockTime', nextActualUndockTime);
            }
          }

          if (sameLeg && updatedStatus.updatedAt !== undefined) {
            const nextUpdatedAt = normalizeGridTextValue(
              updatedStatus.updatedAt,
            );

            if (nextUpdatedAt !== row.updatedAt) {
              assign('updatedAt', nextUpdatedAt);
            }
          }

          if (
            sameLeg &&
            updatedStatus.lastUpdatedBy !== undefined &&
            updatedStatus.lastUpdatedBy !== row.updatedBy
          ) {
            assign('updatedBy', updatedStatus.lastUpdatedBy);
          }

          if (sameLeg && updatedStatus.carGateNumber !== undefined) {
            const nextCarGateNumber = normalizeGridTextValue(
              updatedStatus.carGateNumber,
            );

            if (nextCarGateNumber !== row.carGateNumber) {
              assign('carGateNumber', nextCarGateNumber);
            }
          }

          return nextRow;
        });

        return didChange ? nextRows : prev;
      });
    },
    [location.id, props.onTerminalStatusUpdated],
  );

  const handleDriverDetailsUpdate = useCallback(
    (updatedFields: {
      id: string | number;
      driverName: string;
      driverPhoneNumber: string;
      carRegistrationNumber: string;
      trailerRegistrationNumber: string;
    }) => {
      setRowsData((prev) =>
        prev.map((r) =>
          r.legId === updatedFields.id
            ? {
                ...r,
                driverName: updatedFields.driverName,
                driverPhoneNumber: updatedFields.driverPhoneNumber,
                carRegistrationNumber: updatedFields.carRegistrationNumber,
                trailerRegistrationNumber:
                  updatedFields.trailerRegistrationNumber,
              }
            : r,
        ),
      );
    },
    [],
  );

  useEffect(() => {
    const prevLegs = prevLegsRef.current;
    prevLegsRef.current = legs;

    const freshRows = buildRows(legs, selectedLocation?.id ?? 0);
    if (!freshRows?.length) return;

    setRowsData((prevData) =>
      prevData.map((item) => {
        // Only update rows whose underlying leg actually changed.
        // Unchanged legs keep the same object reference in the array.
        const currLeg = legs?.find((l) => l.id === item.legId);
        const prevLeg = prevLegs?.find((l) => l.id === item.legId);
        if (currLeg === prevLeg) return item;

        const serverRow = freshRows.find(
          (x: { id: string }) => x.id === item.id,
        );
        return serverRow ? { ...item, ...serverRow } : item;
      }),
    );
  }, [legs]);

  const handleCellEditCommit = React.useCallback(
    async ({ id, field, value }: GridCellEditCommitParams) => {
      const row = rows.find((x: { id: string }) => x.id == id);

      if (
        row == null ||
        field === 'actualArrivalDate' ||
        field === 'actualDepartureDate'
      ) {
        return;
      }

      // Optimistically update so the cell doesn't revert while
      // waiting for the subscription round-trip.
      setRowsData((prev) =>
        prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)),
      );

      await truckFillAndTimeMutation({
        variables: {
          truckFillAndTimeInput: {
            id: row.legId,
            locationId: location.id,
            carGateNumber:
              field === 'carGateNumber' ? (value as string) : undefined,
            trailerGateNumber:
              field === 'trailerGateNumber' ? (value as string) : undefined,
            carRegistrationNumber:
              field === 'carRegistrationNumber' ? (value as string) : undefined,
            trailerRegistrationNumber:
              field === 'trailerRegistrationNumber'
                ? (value as string)
                : undefined,
            operator: field === 'operator' ? (value as string) : undefined,
            terminalNickname:
              field === 'terminalNickname' ? (value as string) : undefined,
            actualCarCapacity:
              field === 'actualCarCapacity'
                ? parseInt(value as string, 10)
                : undefined,
            actualTrailerCapacity:
              field === 'actualTrailerCapacity'
                ? parseInt(value as string, 10)
                : undefined,
            terminalNote:
              field === 'terminalNote' ? (value as string) : undefined,
            actualArrivalTime:
              field === 'actualArrivalTime' && typeof value === 'string'
                ? value.match(/\d{2}:\d{2}(:\d{2})?/)
                  ? (value as string)
                  : null
                : undefined,
            actualArrivalDate:
              field === 'actualArrivalDate' ? (value as string) : undefined,
            actualDepartureDate:
              field === 'actualDepartureDate' ? (value as string) : undefined,
            actualDepartureTime:
              field === 'actualDepartureTime' && typeof value === 'string'
                ? value.match(/\d{2}:\d{2}(:\d{2})?/)
                  ? (value as string)
                  : null
                : undefined,
            loadCar:
              field === 'carPallets' && row.load
                ? parseInt(value as string, 10)
                : undefined,
            loadTrailer:
              field === 'trailerPallets' && row.load
                ? parseInt(value as string, 10)
                : undefined,
            unloadCar:
              field === 'carPallets' && row.unload
                ? parseInt(value as string, 10)
                : undefined,
            unloadTrailer:
              field === 'trailerPallets' && row.unload
                ? parseInt(value as string, 10)
                : undefined,
            loadCarPPL:
              field === 'carPPL' && row.load
                ? parseInt(value as string, 10)
                : undefined,
            loadTrailerPPL:
              field === 'trailerPPL' && row.load
                ? parseInt(value as string, 10)
                : undefined,
            unloadCarPPL:
              field === 'carPPL' && row.unload
                ? parseInt(value as string, 10)
                : undefined,
            unloadTrailerPPL:
              field === 'trailerPPL' && row.unload
                ? parseInt(value as string, 10)
                : undefined,
            loadCages:
              field === 'cages' && row.load
                ? parseInt(value as string, 10)
                : undefined,
            unloadCages:
              field === 'cages' && row.unload
                ? parseInt(value as string, 10)
                : undefined,
            sealNumber: field === 'sealNumber' ? (value as string) : undefined,
            productionGroup:
              field === 'productionGroup' ? (value as string) : undefined,
            terminalResponsible:
              field === 'terminalResponsible' ? (value as string) : undefined,
            priority: field === 'priority' ? (value as string) : undefined,
            receivedStatus:
              field === 'receivedStatus'
                ? (value as RecievedStatus)
                : undefined,
            driverName: field === 'driverName' ? (value as string) : undefined,
          },
        },
      }).catch((err) => {
        if (field === 'sealNumber') {
          setErrorInfo({
            errorInField: true,
            errorMessage: t('validation.updateError'),
          });
        }
        if (field === 'actualDepartureTime') {
          setErrorInfo({
            errorInField: true,
            errorMessage: err.message,
          });
        }
        if (field === 'carPPL' || field === 'trailerPPL') {
          setErrorInfo({
            errorInField: true,
            errorMessage: t('validation.loadPPLIsAbove100'),
          });
          if (row.load && field === 'carPPL') {
            truckFillAndTimeMutation({
              variables: {
                truckFillAndTimeInput: {
                  id: row.legId,
                  locationId: location.id,
                  loadCarPPL: row.loadCarPPL,
                },
              },
            });
          } else if (row.load && field === 'trailerPPL') {
            truckFillAndTimeMutation({
              variables: {
                truckFillAndTimeInput: {
                  id: row.legId,
                  locationId: location.id,
                  loadTrailerPPL: row.loadTrailerPPL,
                },
              },
            });
          } else if (row.unload && field === 'carPPL') {
            truckFillAndTimeMutation({
              variables: {
                truckFillAndTimeInput: {
                  id: row.legId,
                  locationId: location.id,
                  unloadCarPPL: row.unloadCarPPL,
                },
              },
            });
          } else if (row.unload && field === 'trailerPPL') {
            truckFillAndTimeMutation({
              variables: {
                truckFillAndTimeInput: {
                  id: row.legId,
                  locationId: location.id,
                  unloadTrailerPPL: row.unloadTrailerPPL,
                },
              },
            });
          }
        }
      });
    },
    [rows],
  );

  const handleCellFocus = useCallback((id: GridRowId) => {
    setActiveRowId(id);
  }, []);

  const customComponents = {
    Toolbar: TftCustomGridToolbar,
    ColumnsPanel: CustomColumnsPanel,
  };

  const handleUploadClick = React.useCallback(
    (data: {
      routeName: string;
      legId: number;
      subcontractorName: string;
      existingFiles: DocumentFile[];
    }) => {
      setExtraData(data);
      setOpenUploadModal(true);
    },
    [],
  );

  const getBackColor = (percentage: number): string => {
    if (percentage) {
      if (percentage > 0 && percentage < 50) {
        return '#f07267';
      } else if (percentage >= 50 && percentage < 80) {
        return '#eda528';
      } else {
        return '#12de63';
      }
    } else {
      return '#ffffff';
    }
  };

  const hasCagesAndLoadedAny = (leg: any) => {
    return (leg.loadCar?.toString().trim().length > 0 ||
      leg.loadTrailer?.toString().trim().length > 0 ||
      leg.loadCarPPL?.toString().trim().length > 0 ||
      leg.loadTrailerPPL?.toString().trim().length > 0) &&
      leg.loadCages?.toString().trim().length > 0
      ? true
      : false;
  };

  const hasCagesAndUnloadedAny = (leg: any) => {
    return (leg.unloadCar?.toString().trim().length > 0 ||
      leg.unloadTrailer?.toString().trim().length > 0 ||
      leg.unloadCarPPL?.toString().trim().length > 0 ||
      leg.unloadTrailerPPL?.toString().trim().length > 0) &&
      leg.unloadCages?.toString().trim().length > 0
      ? true
      : false;
  };

  const hasAnyLoadUnloadAndCages = (leg: any) => {
    return hasCagesAndUnloadedAny(leg) || hasCagesAndLoadedAny(leg);
  };

  const getChipStatus = (row: any): string => {
    if (row.isNonArrivalMarked) {
      return t('attributes.cancelledStatus');
    }
    if (
      row.receivedStatus === 'PickupKipp' ||
      row.receivedStatus === 'HandedOverKipp'
    ) {
      return t('attributes.kippRegistered');
    }
    if (
      row.receivedStatus === 'ReceivedAndHandled' ||
      row.receivedStatus === 'ReceivedNotStarted' ||
      (hasAnyLoadUnloadAndCages(row) && row.actualArrivalTime)
    ) {
      return t('attributes.registeredStatus');
    }
    if (row.actualArrivalTime) {
      return t('attributes.partialRegisteredStatus');
    }
    return t('attributes.unregisteredStatus');
  };

  const filteredDataGrid = React.useMemo(() => {
    let result = rowsData;

    if (props.isHideKippRegistered) {
      result = result.filter(
        (i) =>
          i.receivedStatus !== RecievedStatus.HandedOverKipp &&
          i.receivedStatus !== RecievedStatus.PickupKipp &&
          !i.isNonArrivalMarked,
      );
    }

    if (props.isHideRegistered) {
      result = result.filter(
        (i) =>
          getChipStatus(i) !== t('attributes.registeredStatus') &&
          !i.isNonArrivalMarked,
      );
    }

    if (props.selectedTrafficType) {
      result = result.filter((row: any) =>
        row.trafficType?.includes(props.selectedTrafficType),
      );
    }

    if (props.isHideDone && configuredDoneStatuses.length > 0) {
      result = result.filter(
        (row: any) => !configuredDoneStatuses.includes(row.terminalStatus),
      );
    }

    return result;
  }, [
    rowsData,
    props.isHideKippRegistered,
    props.isHideRegistered,
    props.selectedTrafficType,
    props.isHideDone,
    configuredDoneStatuses,
  ]);

  const registeredCount = React.useMemo(
    () =>
      filteredDataGrid.filter(
        (i: any) => getChipStatus(i) === t('attributes.registeredStatus'),
      ).length,
    [filteredDataGrid],
  );

  const selectedPrintRows = filteredDataGrid.filter((row) =>
    selectedRowIds.includes(row.id),
  );

  const handlePrintLoadingLists = async () => {
    setIsPreparingPrint(true);
    try {
      await printLoadingLists(selectedPrintRows, httpClient, {
        title: t('attributes.printLoadingLists'),
        preparing: t('attributes.preparingLoadingLists'),
        popupBlocked: t('errors.loadingListPopupBlocked'),
      });
    } catch (error) {
      setErrorInfo({
        errorInField: true,
        errorMessage: `${t('errors.failedToPrintLoadingLists')}${
          error instanceof Error ? ` ${error.message}` : ''
        }`,
      });
    } finally {
      setIsPreparingPrint(false);
    }
  };

  // Compute totals from the visible (hide-filtered) rows.
  // When the DataGrid column filter is active, further restrict to only the
  // rows the DataGrid considers visible via filteredRowsData.
  const totalObj = React.useMemo(() => {
    let records: any[] = filteredDataGrid;

    if (!_.isEmpty(filteredRowsData)) {
      const visibleIds = Object.entries(filteredRowsData)
        .filter(([, visible]) => visible === true)
        .map(([id]) => id);
      records = filteredDataGrid.filter((item: any) =>
        visibleIds.includes(item.id?.toString()),
      );
    }

    return {
      totalLoadPallet:
        (_.sumBy(records, 'loadCar') ?? 0) +
        (_.sumBy(records, 'loadTrailer') ?? 0),
      totalLoadPalletSpace:
        (_.sumBy(records, 'loadCarPPL') ?? 0) +
        (_.sumBy(records, 'loadTrailerPPL') ?? 0),
      totalUnloadPallet:
        (_.sumBy(records, 'unloadCar') ?? 0) +
        (_.sumBy(records, 'unloadTrailer') ?? 0),
      totalUnloadPalletSpace:
        (_.sumBy(records, 'unloadCarPPL') ?? 0) +
        (_.sumBy(records, 'unloadTrailerPPL') ?? 0),
    };
  }, [filteredDataGrid, filteredRowsData]);

  const onChangeColumnVisibilityModel = (
    newModel: GridColumnVisibilityModel,
  ) => {
    if (newModel && !_.isEqual(newModel, tftConfig.gridColumnVisibilityModel)) {
      setTftConfig({ ...tftConfig, gridColumnVisibilityModel: newModel });
      visibilityRef.current = newModel;
    }
  };

  const handleCarArrived = (params: GridRenderCellParams) => {
    const currentDate = format(new Date(), 'yyyy-MM-dd');
    const currentTime = format(new Date(), 'HH:mm');

    if (currentDate)
      updateActualArrivalDate(
        params.row.id,
        params.row.legId,
        currentDate,
        'actualDepartureDate',
        currentTime,
      );
  };

  const updateActualArrivalDate = (
    id: GridRowId,
    legId: number,
    date: string,
    field: string,
    actualArrivalTime?: string,
  ) => {
    if (date) {
      truckFillAndTimeMutation({
        variables: {
          truckFillAndTimeInput: {
            id: legId,
            locationId: location.id,
            actualArrivalDate: date,
            actualArrivalTime:
              actualArrivalTime && typeof actualArrivalTime === 'string'
                ? actualArrivalTime.match(/\d{2}:\d{2}(:\d{2})?/)
                  ? (actualArrivalTime as string)
                  : null
                : undefined,
          },
        },
      }).then(() => {
        apiRef.current.setCellMode(id, field, 'view');
        const row = apiRef.current.getRow(id);
        if (row.actualDepartureDate !== 'null') {
          apiRef.current.setEditCellValue({
            field: 'actualDepartureDate',
            id,
            value: '',
          });
          apiRef.current.setCellMode(id, 'actualDepartureDate', 'view');
        }
      });
    }
  };

  const handleCarDeparted = (params: GridRenderCellParams) => {
    const currentDate = format(new Date(), 'yyyy-MM-dd');
    const currentTime = format(new Date(), 'HH:mm');

    if (currentDate)
      truckFillAndTimeMutation({
        variables: {
          truckFillAndTimeInput: {
            id: params.row.legId,
            locationId: location.id,
            actualDepartureDate: currentDate,
            actualDepartureTime: currentTime,
          },
        },
      })
        .then(() => {
          apiRef.current.setCellMode(params.row.id, params.row.field, 'view');
        })
        .catch((err) => {
          setErrorInfo({
            errorInField: true,
            errorMessage: err.message,
          });
        });
  };
  const baseColumns = React.useMemo(
    () =>
      listIncomingColumns({
        apiRef,
        classes,
        t,
        httpClient,
        globalDateFormat,
        locationId: location.id,
        truckFillAndTimeMutation,
        tftConfig,
        visibilityRef,
        setErrorInfo,
        getChipStatus,
        handleUploadClick,
        handleCellChecked,
        handleSealBrokenChecked,
        updateActualArrivalDate,
        handleCarArrived,
        handleCarDeparted,
        getBackColor,
        canEditCell,
        setActiveTerminalStatusLegId,
        onNorwayColumnsUpdate: handleNorwayColumnsUpdate,
        onDriverDetailsUpdate: handleDriverDetailsUpdate,
        noTextWrap: !expandRowHeight,
      }),
    [
      apiRef,
      classes,
      t,
      httpClient,
      globalDateFormat,
      location.id,
      truckFillAndTimeMutation,
      tftConfig,
      visibilityRef,
      getChipStatus,
      handleUploadClick,
      handleCellChecked,
      handleSealBrokenChecked,
      updateActualArrivalDate,
      handleCarArrived,
      handleCarDeparted,
      getBackColor,
      handleNorwayColumnsUpdate,
      handleDriverDetailsUpdate,
      expandRowHeight,
    ],
  );

  const columns = React.useMemo(() => {
    if (!isAdjustedColumnWidth) {
      return baseColumns;
    }

    // Preserve the widths captured before adjustment.
    const originalWidths = originalColumnWidthsRef.current ?? {};

    const expandedColumnWidths: Record<string, number> = {
      timeWhenReceived: 240,
      kippTimeWhenReceived: 240,
    };

    return baseColumns.map((column) => {
      if (column.field.startsWith('__')) {
        return column;
      }

      const originalWidth =
        originalWidths[column.field] ?? column.width ?? column.minWidth ?? 100;

      const targetWidth = expandedColumnWidths[column.field] ?? 150;

      return {
        ...column,
        width: Math.max(originalWidth, targetWidth),
      };
    });
  }, [baseColumns, isAdjustedColumnWidth]);

  const onExportTFTExcelClicked = useCallback(() => {
    const delimiter = ';#';
    const csvString = apiRef.current.getDataAsCsv({
      delimiter,
      getRowsToExport: () => gridFilteredSortedRowIdsSelector(apiRef),
    });

    // Collapse newlines inside quoted CSV fields so they stay in one cell
    const sanitizedCsv = csvString.replace(/"[^"]*"/g, (match) =>
      match.replace(/\r?\n/g, ' '),
    );

    const arrayOfArrayCsv = sanitizedCsv.split('\n').map((row: string) => {
      return row.split(delimiter);
    });

    if (arrayOfArrayCsv.length > 1) {
      const allHeaders = arrayOfArrayCsv[0].map((str) => {
        return str.toLowerCase();
      });

      let priceColumnIndex = allHeaders.indexOf('agreed price');

      if (priceColumnIndex == -1)
        priceColumnIndex = allHeaders.indexOf('agreed price\r');

      if (priceColumnIndex >= 0) {
        for (let i = 1; i < arrayOfArrayCsv.length; i++) {
          if (
            arrayOfArrayCsv[i][priceColumnIndex] &&
            typeof arrayOfArrayCsv[i][priceColumnIndex] !== 'undefined' &&
            arrayOfArrayCsv[i][priceColumnIndex].toString().trim().length > 3 &&
            arrayOfArrayCsv[i][priceColumnIndex].toString().trim().includes(' ')
          )
            arrayOfArrayCsv[i][priceColumnIndex] = arrayOfArrayCsv[i][
              priceColumnIndex
            ].replace(/ /g, '');
        }
      }
    }

    const wb = XLSX.utils.book_new();
    const newWs = XLSX.utils.aoa_to_sheet(arrayOfArrayCsv);
    XLSX.utils.book_append_sheet(wb, newWs);
    XLSX.writeFile(wb, `${TABLE_NAMES.TruckFillAndTime}.xlsx`);
  }, [apiRef]);

  return (
    <div style={{ height: '100%' }}>
      <Grid
        container
        style={{ marginTop: 5, marginBottom: 5 }}
        alignItems="center"
        justifyContent="flex-start"
      >
        {/* <Grid item xs={2} container justifyContent="flex-start">
          <Button
            variant="contained"
            onClick={updateTftConfiguration}
            disabled={configStoreLoading}
          >
            {configStoreLoading
              ? `${t('states.loading')}...`
              : t('button.save', { item: 'Config.' })}
          </Button>
        </Grid> */}
        {/* {(nextConfig: any) => { 
            setTftConfig((prev) => ({
              ...prev,
              ...nextConfig,
              gridColumnVisibilityModel:
                nextConfig?.gridColumnVisibilityModel ??
                nextConfig?.colVisibility ??
                prev.gridColumnVisibilityModel,
            }));
           } */}
        <Grid item xs={3}>
          <Typography style={{ fontWeight: 'bolder', display: 'inline-block' }}>
            {`LOAD  >> Total PAL: `}
            <Typography
              style={{
                fontWeight: 'bolder',
                color: '#69bc46',
                display: 'inline-block',
              }}
            >
              {totalObj.totalLoadPallet}
            </Typography>
            {` | Total PPL: `}
            <Typography
              style={{
                fontWeight: 'bolder',
                color: '#69bc46',
                display: 'inline-block',
              }}
            >
              {totalObj.totalLoadPalletSpace}
            </Typography>
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography style={{ fontWeight: 'bolder', display: 'inline-block' }}>
            {`UNLOAD >> Total PAL: `}
            <Typography
              style={{
                fontWeight: 'bolder',
                color: '#69bc46',
                display: 'inline-block',
              }}
            >
              {totalObj.totalUnloadPallet}
            </Typography>
            {` | Total PPL: `}
            <Typography
              style={{
                fontWeight: 'bolder',
                color: '#69bc46',
                display: 'inline-block',
              }}
            >
              {totalObj.totalUnloadPalletSpace}
            </Typography>
          </Typography>
        </Grid>
        <Grid item xs={3}>
          <Typography style={{ fontWeight: 'bolder', display: 'inline-block' }}>
            {` REG. Cars: `}
            <Typography
              style={{
                fontWeight: 'bolder',
                color: '#69bc46',
                display: 'inline-block',
              }}
            >
              {registeredCount}
            </Typography>
          </Typography>
        </Grid>
      </Grid>
      <Box style={{ height: height }}>
        {rowsData && columns.length ? (
          <div
            style={{ height: '100%' }}
            onBlur={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                setActiveRowId(null);
              }
            }}
          >
            <DataGridPro
              loading={loading && legs == null}
              error={error}
              apiRef={apiRef}
              components={customComponents}
              onCellClick={(params) => handleCellFocus(params.id)}
              onCellKeyDown={(params) => handleCellFocus(params.id)}
              onCellEditCommit={handleCellEditCommit}
              disableSelectionOnClick
              checkboxSelection
              selectionModel={selectedRowIds}
              onSelectionModelChange={setSelectedRowIds}
              rowHeight={scaledRowHeight}
              headerHeight={scaledHeaderHeight}
              sx={{
                '& .MuiDataGrid-cell': {
                  fontSize: scaledFontSize,
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  fontSize: scaledFontSize,
                },
              }}
              componentsProps={{
                columnMenu: {
                  background: 'red',
                  counter: rows.length,
                },
                toolbar: {
                  onExportTFTExcelClicked,
                  printLoadingLists: {
                    onPrint: handlePrintLoadingLists,
                    disabled: selectedPrintRows.length === 0,
                    loading: isPreparingPrint,
                  },
                  toggleRowHeight: {
                    isChecked: expandRowHeight,
                    onToggleRowHeight: () =>
                      setExpandRowHeight((prev) => !prev),
                  },
                  toggleColumnWidth: {
                    isAdjusted: isAdjustedColumnWidth,
                    onToggleColumnWidth: handleToggleColumnWidth,
                  },
                },
              }}
              columnTypes={{
                string: {
                  filterOperators: [
                    ...getGridStringOperators(),
                    doesNotContainOperator,
                    doesNotEqualOperator,
                  ],
                },
              }}
              autoHeight={autoHeights}
              getRowHeight={() => (expandRowHeight ? 'auto' : scaledRowHeight)}
              isCellEditable={(params: GridCellParams) => {
                const field = params.field;
                return canEditCell(params.row, field);
              }}
              columns={columns}
              rows={filteredDataGrid}
              getRowClassName={(params) =>
                params.id === activeRowId
                  ? classes.muiCurrent
                  : params.indexRelativeToCurrentPage % 2 === 0
                  ? classes.muiEven
                  : classes.muiOdd
              }
              density={tftConfig.density ?? density}
              filterModel={tftConfig.filterModel}
              sortModel={tftConfig.sortModel}
              columnVisibilityModel={tftConfig.gridColumnVisibilityModel ?? {}}
              pinnedColumns={tftConfig.pinnedColumns}
              onPinnedColumnsChange={(model: GridPinnedColumns) => {
                setTftConfig({ ...tftConfig, pinnedColumns: model });
              }}
              onFilterModelChange={(model) =>
                setTftConfig({ ...tftConfig, filterModel: model })
              }
              onSortModelChange={(model: GridSortModel) =>
                setTftConfig({ ...tftConfig, sortModel: model })
              }
              onStateChange={(state) => {
                const newDensity = state.density?.value;
                if (newDensity) {
                  setTftConfig((prev: any) =>
                    prev.density === newDensity
                      ? prev
                      : { ...prev, density: newDensity },
                  );
                }
                const visibleRows = state.filter?.visibleRowsLookup;
                if (visibleRows && visibleRows !== filteredRowsData) {
                  setFilteredRowsData(visibleRows);
                }
              }}
              onColumnOrderChange={() => handleColumnOrderChange()}
              onColumnWidthChange={() => handleWidthChange()}
              onColumnVisibilityModelChange={onChangeColumnVisibilityModel}
            />
          </div>
        ) : null}
        <div>
          {openUploadModal ? (
            <UploadFileModal
              open={openUploadModal}
              handleClose={() => {
                setOpenUploadModal(false);
              }}
              extraData={extraData}
              showLoading={showUploadLoading}
              onUploadStart={async (legId, files) => {
                if (legId === 0) return;

                setShowUploadLoading(true);
                try {
                  const result = await httpClient.uploadLegImages(files, legId);
                  const attachedFiles = result.data
                    .files as unknown as DocumentFile[];

                  setExtraData((current) =>
                    current
                      ? {
                          ...current,
                          existingFiles: attachedFiles,
                        }
                      : current,
                  );
                  setRowsData((current) =>
                    current.map((row) =>
                      row.legId === legId
                        ? {
                            ...row,
                            files: attachedFiles,
                          }
                        : row,
                    ),
                  );
                  await refetchLatestLeg({
                    variables: { legId, locationId: location.id },
                  });
                } catch (error: any) {
                  setErrorInfo({
                    errorInField: true,
                    errorMessage: error?.message || 'Image upload failed.',
                  });
                  throw error;
                } finally {
                  setShowUploadLoading(false);
                }
              }}
              onDeleteClicked={async (legId, fileId) => {
                setShowUploadLoading(true);
                try {
                  await deleteAttachmentFileMutation({
                    variables: {
                      input: {
                        entityId: legId,
                        entityType: 'leg',
                        fileId,
                      },
                    },
                  });

                  setExtraData((current) =>
                    current
                      ? {
                          ...current,
                          existingFiles: current.existingFiles.filter(
                            (file) => file.id !== fileId,
                          ),
                        }
                      : current,
                  );
                  setRowsData((current) =>
                    current.map((row) =>
                      row.legId === legId
                        ? {
                            ...row,
                            files: (row.files ?? []).filter(
                              (file) => file.id !== fileId,
                            ),
                          }
                        : row,
                    ),
                  );
                  await refetchLatestLeg({
                    variables: { legId, locationId: location.id },
                  });
                } catch (error: any) {
                  setErrorInfo({
                    errorInField: true,
                    errorMessage: error?.message || 'Image deletion failed.',
                  });
                  throw error;
                } finally {
                  setShowUploadLoading(false);
                }
              }}
            />
          ) : null}
        </div>
        {selectedTerminalStatusRow ? (
          <TerminalStatusModal
            row={selectedTerminalStatusRow}
            locationId={location.id}
            onStatusUpdated={handleTerminalStatusUpdated}
            isOpen
            onClose={() => setActiveTerminalStatusLegId(null)}
            onError={(message) =>
              setErrorInfo({
                errorInField: true,
                errorMessage: message,
              })
            }
          />
        ) : null}
      </Box>
      <Snackbar
        open={errorInfo?.errorInField}
        key={'alert'}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        autoHideDuration={5000}
        onClose={() => {
          setErrorInfo({ errorInField: false, errorMessage: '' });
        }}
      >
        <Alert
          onClose={() => {
            setErrorInfo({ errorInField: false, errorMessage: '' });
          }}
          severity="error"
        >
          {errorInfo?.errorMessage}
        </Alert>
      </Snackbar>
      <Snackbar
        open={configStatus.status ? true : false}
        key={configStatus.status == 'Success' ? 'success' : 'alert'}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        autoHideDuration={5000}
        onClose={() => {
          setConfigStatus({ status: '' });
        }}
      >
        <Alert
          onClose={() => {
            setConfigStatus({ status: '' });
          }}
          severity={configStatus.status == 'Success' ? 'success' : 'error'}
        >
          {configStatus.status == 'Success'
            ? t('attributes.saveConfigSuccessMessage')
            : t('attributes.failConfigMessage')}
        </Alert>
      </Snackbar>
    </div>
  );
}

function canEditCell(
  leg: {
    capacity?: number;
    load?: boolean;
    unload?: boolean;
    isNonArrivalMarked?: boolean;
    isSealBroken?: boolean;
    isLoadingInCar?: boolean;
    isLoadingInTrailer?: boolean;
  },
  field: string,
): boolean {
  if (field == 'actualCarCapacity' && !leg.isLoadingInCar) return false;
  if (
    (field == 'actualTrailerCapacity' || field == 'trailerGateNumber') &&
    !leg.isLoadingInTrailer
  )
    return false;
  if (
    field == 'carPallets' ||
    field == 'trailerPallets' ||
    field == 'carPPL' ||
    field == 'trailerPPL'
  ) {
    if (leg.load && leg.unload) {
      return false;
    } else if (leg.load || leg.unload) {
      return true;
    } else {
      return false;
    }
  }
  if (leg.isNonArrivalMarked) {
    return false;
  }

  if (field === 'sealNumber') {
    if (leg.unload === true && leg.isSealBroken === true) return true;
    if (leg.load === true) return true;
    if (leg.load === false) return false;

    return true;
  }
  return true;
}

function getRecentPreviousLoadLeg(
  legs: NonNullable<ListIncomingRoutesProps['legs']>,
) {
  const thisLeg = legs[0];
  if (thisLeg.position != null) {
    let previousLegPosition = thisLeg.position === 0 ? 0 : thisLeg.position - 1;
    for (previousLegPosition; previousLegPosition >= 0; previousLegPosition--) {
      const newRouteLeg = thisLeg.route.legs.find(
        (leg) =>
          leg.position === previousLegPosition &&
          leg.load &&
          !leg.unload &&
          leg.deletedAt == null,
      );
      if (newRouteLeg) {
        return newRouteLeg;
      }
    }
  } else {
    thisLeg.route.legs.find((leg) => leg.position === 0);
  }
}

function buildRows(legs: ListIncomingRoutesProps['legs'], locationId: number) {
  return (
    legs?.map((routeLeg) => {
      const { route } = routeLeg;
      const leg = routeLeg;

      const {
        id,
        routeId,
        capacity,
        type,
        routeCreatedBy,
        routeDeletedAt,
        routeDeletedBy,
        routeLastUpdatedBy,
        routeUpdatedAt,
      } = route;
      const routeCountries = route.legs
        .filter((routeLegItem) => routeLegItem.deletedAt == null)
        .map((routeLegItem) =>
          normalizeRouteCountry(routeLegItem.location?.country),
        )
        .filter((country): country is string => country.length > 0);
      const isCrossborder = new Set(routeCountries).size > 1;
      const activeRouteLegsByPosition = route.legs
        .filter((routeLegItem) => routeLegItem.deletedAt == null)
        .sort((leftLeg, rightLeg) => {
          const leftPosition = leftLeg.position ?? Number.MAX_SAFE_INTEGER;
          const rightPosition = rightLeg.position ?? Number.MAX_SAFE_INTEGER;
          return leftPosition - rightPosition;
        });
      const currentLegIndexForGeo =
        leg.position == null
          ? -1
          : activeRouteLegsByPosition.findIndex(
              (routeLegItem) => routeLegItem.position === leg.position,
            );
      const previousLegForGeo =
        currentLegIndexForGeo > 0
          ? activeRouteLegsByPosition[currentLegIndexForGeo - 1]
          : undefined;
      const nextLegForGeo =
        currentLegIndexForGeo >= 0 &&
        currentLegIndexForGeo < activeRouteLegsByPosition.length - 1
          ? activeRouteLegsByPosition[currentLegIndexForGeo + 1]
          : undefined;
      const departureGeoLeg = previousLegForGeo ?? leg;
      const arrivalGeoLeg = nextLegForGeo ?? leg;
      const trafficType = getTrafficTypeFromLegType(
        leg,
        route.legs,
        isCrossborder,
      );
      const carActCapacity = leg.carDetails
        ? leg.carDetails.actualCapacity
        : leg.actualCapacity
        ? leg.actualCapacity
        : 0;
      const trailerActCapacity = leg.trailerDetails
        ? leg.trailerDetails.actualCapacity
        : 0;
      const routeCapacity =
        carActCapacity || trailerActCapacity
          ? (carActCapacity || 0) + (trailerActCapacity || 0)
          : capacity;
      const isProductionDayToday =
        leg.transportationDate &&
        leg.transportationDate === format(new Date(), DATE_FORMAT);

      const firstLegLocation =
        leg != null
          ? getRecentPreviousLoadLeg([leg])
          : route.legs.find(
              (leg) => leg.position === 0 && leg.deletedAt == null,
            );

      const legPosition = leg.position;
      const nextLegLocation =
        typeof legPosition !== 'undefined'
          ? route.legs
              .filter((l) => l.deletedAt == null)
              .find((l) => l.position === legPosition + 1)
          : leg;
      const lastLegLocation = route.legs
        .filter((l) => l.deletedAt == null)
        .reduce((prev, current) => {
          if (typeof prev.position === 'undefined' || prev.position === null)
            return current;
          if (
            typeof current.position === 'undefined' ||
            current.position === null
          )
            return prev;
          return current.position > prev.position ? current : prev;
        });
      const routeFirstLeg = route.legs.find(
        (l) => l.position === 0 && l.deletedAt == null,
      );
      const firstLegTerminal = routeFirstLeg?.location?.name ?? '';
      const lastLegTerminal = lastLegLocation?.location?.name ?? '';
      const departureCity = departureGeoLeg?.location?.city;
      const departureCountry = normalizeRouteCountry(
        departureGeoLeg?.location?.country,
      );
      const departureCountryInitial =
        firstLegLocation?.location && firstLegLocation?.location?.country
          ? firstLegLocation?.location.country
          : '';
      const arrivalCity = arrivalGeoLeg?.location?.city;
      const arrivalCountry = normalizeRouteCountry(
        arrivalGeoLeg?.location?.country,
      );
      const toCountryInitial =
        lastLegLocation?.location && lastLegLocation?.location?.country
          ? lastLegLocation?.location.country
          : '';
      const fromToToCountry = `${departureCountryInitial.toLocaleUpperCase()}-${toCountryInitial.toLocaleUpperCase()}`;
      const departureTerminal =
        firstLegLocation?.location && firstLegLocation?.location.name
          ? firstLegLocation?.location.name
          : '';
      const nextLegLocationName = (
        nextLegLocation?.location as { name?: string } | undefined
      )?.name;
      const arrivingTerminal =
        nextLegLocationName ?? lastLegLocation?.location?.name ?? '';
      const subcontractorId = route.subcontractorLocation.id;
      const subcontractorName = route.subcontractorLocation.name;
      const loadSumPPI = (leg.loadCar || 0) + (leg.loadTrailer || 0);
      const unloadSumPPI = (leg.unloadCar || 0) + (leg.unloadTrailer || 0);

      const loadLoadFactor =
        routeCapacity == null || loadSumPPI === 0 || routeCapacity === 0
          ? ''
          : Math.floor((loadSumPPI / routeCapacity) * 100);

      const unloadLoadFactor =
        routeCapacity == null || unloadSumPPI === 0 || routeCapacity === 0
          ? ''
          : Math.floor((unloadSumPPI / routeCapacity) * 100);

      //for PPL
      const loadPPLSum = (leg.loadCarPPL || 0) + (leg.loadTrailerPPL || 0);
      const unloadPPLSum =
        (leg.unloadCarPPL || 0) + (leg.unloadTrailerPPL || 0);

      const loadPPLFactor =
        routeCapacity == null || loadPPLSum === 0 || routeCapacity === 0
          ? ''
          : Math.floor((loadPPLSum / routeCapacity) * 100);

      const unloadPPLFactor =
        routeCapacity == null || unloadPPLSum === 0 || routeCapacity === 0
          ? ''
          : Math.floor((unloadPPLSum / routeCapacity) * 100);

      return {
        id: `${id}-${leg.id}-${leg.arrivalTime}`,
        legId: leg.id,
        routeId: routeId,
        routeId2: id,
        type,
        capacity,
        carGateNumber: leg.carDetails?.gateNumber
          ? leg.carDetails?.gateNumber
          : leg.gateNumber
          ? leg.gateNumber
          : '',
        trailerGateNumber: leg.trailerDetails?.gateNumber
          ? leg.trailerDetails?.gateNumber
          : '',
        trailerRegistrationNumber: leg.trailerDetails?.registrationNumber
          ? leg.trailerDetails?.registrationNumber
          : leg.trailerRegistrationNumber,
        carRegistrationNumber:
          leg.carDetails?.registrationNumber ?? leg.carRegistrationNumber ?? '',
        locationType: leg.location?.type,
        terminalNickname: leg.terminalNickname,
        actualCapacity: leg.actualCapacity,
        load: leg.load,
        unload: leg.unload,
        departureTerminal,
        arrivingTerminal,
        firstLegTerminal,
        lastLegTerminal,
        arrivingTerminalLocationCode:
          nextLegLocation?.location?.locationShortCode ?? '',
        departureCity,
        arrivalCity,
        departureCountry,
        arrivalCountry,
        fromToToCountry,
        arrivalTime: leg.arrivalTime,
        departureTime: timeOrNull(leg.departureTime),
        departureTerminalLocationCode:
          firstLegLocation?.location?.locationShortCode ?? '',
        subcontractorId,
        subcontractorName,
        loadType: resolveLoadTypeFromLeg(leg),
        carPallets:
          leg.load && leg.unload
            ? undefined
            : leg.load
            ? leg.loadCar
            : leg.unload
            ? leg.unloadCar
            : undefined,
        trailerPallets:
          leg.load && leg.unload
            ? undefined
            : leg.load
            ? leg.loadTrailer
            : leg.unload
            ? leg.unloadTrailer
            : undefined,
        palletsFactor:
          leg.load && leg.unload
            ? undefined
            : leg.load
            ? loadLoadFactor
            : leg.unload
            ? unloadLoadFactor
            : undefined,
        carPPL:
          leg.load && leg.unload
            ? undefined
            : leg.load
            ? leg.loadCarPPL
            : leg.unload
            ? leg.unloadCarPPL
            : undefined,
        trailerPPL:
          leg.load && leg.unload
            ? undefined
            : leg.load
            ? leg.loadTrailerPPL
            : leg.unload
            ? leg.unloadTrailerPPL
            : undefined,
        PPLFactor:
          leg.load && leg.unload
            ? undefined
            : leg.load
            ? loadPPLFactor
            : leg.unload
            ? unloadPPLFactor
            : undefined,
        cages:
          leg.load && leg.unload
            ? undefined
            : leg.load
            ? leg.loadCages
            : leg.unload
            ? leg.unloadCages
            : undefined,
        actualArrivalTime: leg.actualArrivalTime ?? '',
        actualArrivalDate: leg.actualArrivalDate ?? '',
        truckStatusBasedOnTime: leg.arrivalStatus ?? '',
        actualDepartureTime: leg.actualDepartureTime ?? '',
        actualDepartureDate: leg.actualDepartureDate ?? '',
        actualDockTime: leg.actualDockTime ?? '',
        actualDockDate: leg.actualDockDate ?? '',
        actualUndockTime: leg.actualUndockTime ?? '',
        actualUndockDate: leg.actualUndockDate ?? '',
        carCarrierId: route.carCarrierId ?? '',
        trailerCarrierId: route.trailerCarrierId ?? '',
        loadCar: leg.loadCar,
        loadCarPPL: leg.loadCarPPL,
        loadCages: leg.loadCages,
        loadTrailer: leg.loadTrailer,
        loadTrailerPPL: leg.loadTrailerPPL,
        loadSumPPI,
        loadLoadFactor,
        loadPPLSum,
        loadPPLFactor,
        position: leg.position,
        location: leg.location,
        route: leg.route,
        unloadCar: leg.unloadCar,
        unloadCarPPL: leg.unloadCarPPL,
        unloadCages: leg.unloadCages,
        unloadTrailer: leg.unloadTrailer,
        unloadTrailerPPL: leg.unloadTrailerPPL,
        unloadSumPPI,
        unloadLoadFactor,
        unloadPPLSum,
        unloadPPLFactor,
        note: leg.note,
        tourId: route?.tourRoute?.tourId,
        isCreatedFromTerminal: route?.isCreatedFromTerminal,
        isCreatedFromPlanned: route?.isCreatedFromPlanned,
        terminalNote: leg.terminalNote,
        updatedBy: leg.lastUpdatedBy,
        updatedAt: leg.updatedAt,
        sumOfPallets: 0,
        isContainDangerousGoods: false,
        productionDate: leg.productionDate,
        transportationDate: leg.transportationDate,
        isProductionDayToday: isProductionDayToday,
        isNonArrivalMarked: leg.isNonArrivalMarked ?? false,
        nonArrivalMarkedFrom: leg.nonArrivalMarkedFrom ?? '',
        sealNumber: leg.sealNumber ?? '',
        isSealBroken: leg.isSealBroken ?? false,
        files: leg.files,
        trafficType,
        crossborder: isCrossborder ? 'Yes' : 'No',
        productionGroup: leg.productionGroup,
        operator: leg.operator,
        terminalResponsible: leg.terminalResponsible,
        priority: leg.priority ?? '',
        terminalStatus: leg.terminalStatus,
        terminalStatusDescription: leg.terminalStatusDescription ?? '',
        terminalStatusUpdatedAt: leg.terminalStatusUpdatedAt ?? '',
        terminalStatusLog: leg.terminalStatusLog ?? [],
        currentTerminalStatus:
          getLatestTerminalStatusLogForTerminal(
            leg.terminalStatusLog,
            locationId ?? 0,
          )?.status ?? '',
        currentTerminalStatusDescription:
          formatTerminalStatusDescriptionFromLog(
            getLatestTerminalStatusLogForTerminal(
              leg.terminalStatusLog,
              locationId ?? 0,
            ),
          ),
        receivedStatus: leg.receivedStatus,
        receivedBy: leg.receivedBy,
        timeWhenReceived: leg.timeWhenReceived,
        kippReceivedBy: leg.kippReceivedBy,
        kippTimeWhenReceived: leg.kippTimeWhenReceived,
        arrivingPallets: 0,
        arrivingPalletSpace: 0,
        timeObj: leg.timeObj,
        routeCreatedBy,
        routeDeletedAt,
        routeDeletedBy,
        routeLastUpdatedBy,
        routeUpdatedAt,
        //these below fields are from first leg and the logic handled in backend
        inneholdsbeskrivelse: leg.route.inneholdsbeskrivelse,
        turnummer: leg.route?.turnummer,
        rutekode: leg.route?.rutekode,
        togref: leg?.togref,
        containerId: leg?.containerId,
        tom: leg?.tom,
        assigned: leg?.assigned,
        ansvBefraktare: leg?.ansvBefraktare,
        timeWhenAssigned: leg?.timeWhenAssigned,
        //coming from first leg loadinglist volumne
        // Fall back to carDetails.loadingListItem[0].volume when top-level fields are null
        // (backend populates these from firstLegLocation; nested carDetails carries the same data)
        bpx: leg?.bpx ?? leg?.carDetails?.loadingListItem?.[0]?.volume?.bpx,
        pri03:
          leg?.pri03 ?? leg?.carDetails?.loadingListItem?.[0]?.volume?.pri03,
        pri49:
          leg?.pri49 ?? leg?.carDetails?.loadingListItem?.[0]?.volume?.pri49,
        upri03:
          leg?.upri03 ?? leg?.carDetails?.loadingListItem?.[0]?.volume?.upri03,
        upri49:
          leg?.upri49 ?? leg?.carDetails?.loadingListItem?.[0]?.volume?.upri49,
        parti:
          leg?.parti ?? leg?.carDetails?.loadingListItem?.[0]?.volume?.parti,
        brev: leg?.brev ?? leg?.carDetails?.loadingListItem?.[0]?.volume?.brev,
        pru: leg?.pru ?? leg?.carDetails?.loadingListItem?.[0]?.volume?.pru,
        pip: leg?.pip ?? leg?.carDetails?.loadingListItem?.[0]?.volume?.pip,
        firstLegCarDetailId: leg?.firstLegCarDetailId,
        firstLegId: leg?.firstLegId,
        adr: leg?.route?.adr,
        trainNumber: leg?.route?.trainNumber,
        //till here
        transportType: leg.transportType,
        carDetailId: leg.carDetails?.id ?? 0,
        isLoadingInCar: leg.isLoadingInCar,
        isLoadingInTrailer: leg.isLoadingInTrailer,
        actualCarCapacity: carActCapacity ?? '',
        actualTrailerCapacity: trailerActCapacity ?? '',
        driverName: leg.driverName ?? leg.route.driverName ?? '',
        isUnplannedRoute: leg.route.isUnplannedRoute ?? false,
      };
    }) || []
  );
}

function normalizeRouteCountry(country?: string | null): string {
  if (!country) {
    return '';
  }
  return (
    COUNTRIES_MAP[country as keyof typeof COUNTRIES_MAP] ?? country
  ).toUpperCase();
}

function getTrafficTypeFromLegType(
  currentLeg: {
    position?: number | null;
    load: boolean;
    unload: boolean;
  },
  routeLegs: {
    position?: number | null;
    load: boolean;
    unload: boolean;
    deletedAt?: string | null;
  }[],
  isCrossborder: boolean,
): TrafficType | '' {
  if (currentLeg.load) {
    return isCrossborder ? TRAFFIC_TYPES.Export : TRAFFIC_TYPES.Outbound;
  }

  if (currentLeg.unload) {
    return isCrossborder ? TRAFFIC_TYPES.Import : TRAFFIC_TYPES.Inbound;
  }

  if (currentLeg.position == null) {
    return '';
  }

  const activeRouteLegs = routeLegs.filter(
    (routeLeg) => routeLeg.deletedAt == null,
  );
  for (
    let previousPosition = currentLeg.position - 1;
    previousPosition >= 0;
    previousPosition--
  ) {
    const previousLeg = activeRouteLegs.find(
      (routeLeg) => routeLeg.position === previousPosition,
    );

    if (!previousLeg) {
      continue;
    }

    if (previousLeg.load) {
      return isCrossborder ? TRAFFIC_TYPES.Export : TRAFFIC_TYPES.Outbound;
    }

    if (previousLeg.unload) {
      return isCrossborder ? TRAFFIC_TYPES.Import : TRAFFIC_TYPES.Inbound;
    }
  }

  return '';
}
